import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardListIcon, ChevronRightIcon, SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from 'lucide-react';
import Button from '../../components/ui/Button';
// Mock order data
const orders = [{
  id: 'JC123456789',
  date: new Date('2023-12-15T12:30:00'),
  status: 'delivered',
  items: [{
    name: 'Fried Egg with Tomato',
    quantity: 2
  }, {
    name: 'Dumplings',
    quantity: 1
  }],
  total: 22.5
}, {
  id: 'JC123456788',
  date: new Date('2023-12-10T13:45:00'),
  status: 'delivered',
  items: [{
    name: 'Fried Egg with Tomato',
    quantity: 1
  }],
  total: 10.5
}, {
  id: 'JC123456787',
  date: new Date('2023-12-05T18:15:00'),
  status: 'cancelled',
  items: [{
    name: 'Kung Pao Chicken',
    quantity: 1
  }, {
    name: 'Rice',
    quantity: 1
  }],
  total: 15.75
}];
const OrderHistory: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-777" />;
      case 'cancelled':
        return <XCircleIcon size={16} className="text-red-500" data-id="element-778" />;
      default:
        return <ClockIcon size={16} className="text-yellow-500" data-id="element-779" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Processing';
    }
  };
  const filteredOrders = orders.filter(order => {
    // Filter by status
    if (filter !== 'all' && order.status !== filter) return false;
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!order.id.toLowerCase().includes(query) && !order.items.some(item => item.name.toLowerCase().includes(query))) {
        return false;
      }
    }
    return true;
  });
  return <div className="max-w-4xl mx-auto" data-id="element-780">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6" data-id="element-781">
        <h1 className="text-2xl font-bold mb-2 sm:mb-0" data-id="element-782">Order History</h1>
        <Button variant="outline" onClick={() => navigate('/food')} className="text-sm" data-id="element-783">
          Order Again
        </Button>
      </div>
      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow-md mb-6" data-id="element-784">
        <div className="p-4 border-b border-gray-200" data-id="element-785">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-786">
            <div className="relative flex-1" data-id="element-787">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-788">
                <SearchIcon size={18} className="text-gray-400" data-id="element-789" />
              </div>
              <input type="text" placeholder="Search orders..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#E84118] focus:border-[#E84118]" data-id="element-790" />
            </div>
            <div className="relative" data-id="element-791">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-792">
                <FilterIcon size={18} className="text-gray-400" data-id="element-793" />
              </div>
              <select value={filter} onChange={e => setFilter(e.target.value)} className="pl-10 pr-8 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#E84118] focus:border-[#E84118] appearance-none bg-white" data-id="element-794">
                <option value="all" data-id="element-795">All Orders</option>
                <option value="delivered" data-id="element-796">Delivered</option>
                <option value="processing" data-id="element-797">Processing</option>
                <option value="cancelled" data-id="element-798">Cancelled</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-799">
                <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor" data-id="element-800">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-801" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Orders list */}
      {filteredOrders.length > 0 ? <div className="space-y-4" data-id="element-802">
          {filteredOrders.map(order => <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow" data-id="element-803">
              <div className="p-4 border-b border-gray-200" data-id="element-804">
                <div className="flex flex-col sm:flex-row justify-between" data-id="element-805">
                  <div className="mb-2 sm:mb-0" data-id="element-806">
                    <div className="flex items-center" data-id="element-807">
                      <ClipboardListIcon size={16} className="text-[#E84118] mr-2" data-id="element-808" />
                      <span className="font-medium" data-id="element-809">{order.id}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-810">
                      {order.date.toLocaleDateString()} at{' '}
                      {order.date.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </p>
                  </div>
                  <div className="flex items-center" data-id="element-811">
                    <div className="flex items-center mr-4" data-id="element-812">
                      {getStatusIcon(order.status)}
                      <span className="ml-1 text-sm" data-id="element-813">
                        {getStatusText(order.status)}
                      </span>
                    </div>
                    <span className="font-bold" data-id="element-814">${order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="p-4" data-id="element-815">
                <div className="mb-3" data-id="element-816">
                  <h3 className="text-sm font-medium text-gray-600 mb-2" data-id="element-817">
                    Items:
                  </h3>
                  <ul className="text-sm" data-id="element-818">
                    {order.items.map((item, index) => <li key={index} className="flex justify-between mb-1" data-id="element-819">
                        <span data-id="element-820">
                          {item.quantity}x {item.name}
                        </span>
                      </li>)}
                  </ul>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200" data-id="element-821">
                  <Button variant="outline" size="sm" onClick={() => {
              // Implement reorder functionality
            }} data-id="element-822">
                    Reorder
                  </Button>
                  <button onClick={() => navigate(`/order/tracking/${order.id}`)} className="flex items-center text-[#E84118] hover:text-[#d13a16] transition-colors text-sm" data-id="element-823">
                    <span data-id="element-824">View Details</span>
                    <ChevronRightIcon size={16} className="ml-1" data-id="element-825" />
                  </button>
                </div>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-lg shadow-md p-6 text-center" data-id="element-826">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-827">
            <ClipboardListIcon size={24} className="text-gray-400" data-id="element-828" />
          </div>
          <h2 className="text-xl font-semibold mb-2" data-id="element-829">No orders found</h2>
          <p className="text-gray-500 mb-6" data-id="element-830">
            {searchQuery || filter !== 'all' ? 'Try changing your search or filter' : "You haven't placed any orders yet"}
          </p>
          <Button onClick={() => navigate('/food')} data-id="element-831">Browse Menu</Button>
        </div>}
    </div>;
};
export default OrderHistory;